import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

const LINK_BTN_STYLE = { background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0 0', color: 'var(--accent)', fontSize: 'var(--text-sm)', fontWeight: 500, width: '100%' };

// Post-login destination for the OAuth resume flow (routes/mcpOAuth.js's
// /authorize bounces an unauthenticated browser here with ?next=/authorize?...
// so it can return to the exact same authorization request once logged in).
// Deliberately NOT a general-purpose redirect param — only ever a literal
// same-path "/authorize?..." is accepted, so this can never become an open
// redirect to an attacker-controlled URL via a crafted `next` value.
function safeNextTarget(searchParams) {
  const next = searchParams.get('next');
  if (next && (next === '/authorize' || next.startsWith('/authorize?'))) return next;
  return null;
}

export default function LoginPage() {
  const { login, requestOtp, verifyOtp } = useAuth();
  const navigate    = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep]         = useState('password'); // 'password' | 'otp-email' | 'otp-code'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode]         = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  async function handlePasswordSubmit(e) {
    e.preventDefault();
    if (!email || !password) { setError('Enter your email and password.'); return; }
    setLoading(true); setError('');
    try {
      const result = await login(email, password);
      if (result.success) {
        const next = safeNextTarget(searchParams);
        if (next) window.location.href = next; else navigate('/');
      }
      else setError(result.error || 'Login failed.');
    } catch {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestOtp(e) {
    e.preventDefault();
    if (!email.trim()) { setError('Enter your email.'); return; }
    setLoading(true); setError('');
    try {
      const result = await requestOtp(email.trim());
      if (result.success) setStep('otp-code');
      else setError(result.error || 'Something went wrong.');
    } catch {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault();
    if (!code.trim()) { setError('Enter the code from your email.'); return; }
    setLoading(true); setError('');
    try {
      const result = await verifyOtp(email.trim(), code.trim());
      if (result.success) {
        const next = safeNextTarget(searchParams);
        if (next) window.location.href = next; else navigate('/');
      }
      else setError(result.error || 'Invalid or expired code.');
    } catch {
      setError('Network error — please try again.');
    } finally {
      setLoading(false);
    }
  }

  const heading = step === 'password' ? 'Welcome back' : step === 'otp-email' ? 'Sign in with a code' : 'Enter your code';
  const sub = step === 'password'
    ? 'Sign in to Social Infinite'
    : step === 'otp-email'
    ? 'Use the email on your Social Infinite account'
    : `We sent a 6-digit code to ${email.trim()}`;

  return (
    <div className="login-shell">
      <div className="login-panel">
        <div className="login-panel-top">
          <div className="login-logo-mark">SI</div>
          <span className="login-panel-product">Social Infinite</span>
        </div>
        <div className="login-panel-body">
          <h2 className="login-panel-headline">Your influencer database, organized.</h2>
          <ul className="login-panel-features">
            <li>Scout creators across Instagram, YouTube &amp; more</li>
            <li>Manage campaigns from briefing to completion</li>
            <li>Track budgets, slots, and deliverables</li>
            <li>Collaborate across your agency team</li>
          </ul>
        </div>
        <p className="login-panel-brand">Social Infinite — Internal tool</p>
      </div>

      <div className="login-form-panel">
        <div className="login-form-inner">
          <h1 className="login-heading">{heading}</h1>
          <p className="login-sub">{sub}</p>

          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} noValidate>
              <div className="form-field">
                <label className="field-label" htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  className="field-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <div className="form-field">
                <label className="field-label" htmlFor="login-password">Password</label>
                <div className="field-input-wrap">
                  <input
                    id="login-password"
                    className="field-input has-suffix"
                    type={showPass ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="field-suffix-btn"
                    onClick={() => setShowPass(s => !s)}
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && <div className="form-error">{error}</div>}

              <button type="submit" className="btn btn-primary login-submit" disabled={loading}>
                {loading
                  ? <><div className="spinner spinner-sm spinner-white" /> Signing in…</>
                  : 'Sign in'}
              </button>

              <button
                type="button"
                style={LINK_BTN_STYLE}
                onClick={() => { setStep('otp-email'); setPassword(''); setError(''); }}
              >
                Sign in with a code instead
              </button>
            </form>
          )}

          {step === 'otp-email' && (
            <form onSubmit={handleRequestOtp} noValidate>
              <div className="form-field">
                <label className="field-label" htmlFor="login-otp-email">Email</label>
                <input
                  id="login-otp-email"
                  className="field-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoFocus
                  autoComplete="email"
                />
              </div>

              {error && <div className="form-error">{error}</div>}

              <button type="submit" className="btn btn-primary login-submit" disabled={loading}>
                {loading ? <><div className="spinner spinner-sm spinner-white" /> Sending code…</> : 'Send login code'}
              </button>

              <button
                type="button"
                style={LINK_BTN_STYLE}
                onClick={() => { setStep('password'); setError(''); }}
              >
                Use password instead
              </button>
            </form>
          )}

          {step === 'otp-code' && (
            <form onSubmit={handleVerifyOtp} noValidate>
              <div className="form-field">
                <label className="field-label" htmlFor="login-otp-code">6-digit code</label>
                <input
                  id="login-otp-code"
                  className="field-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>

              {error && <div className="form-error">{error}</div>}

              <button type="submit" className="btn btn-primary login-submit" disabled={loading}>
                {loading ? <><div className="spinner spinner-sm spinner-white" /> Verifying…</> : 'Sign in'}
              </button>

              <button
                type="button"
                style={LINK_BTN_STYLE}
                onClick={() => { setStep('otp-email'); setCode(''); setError(''); }}
              >
                Use a different email
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
